import React, { useEffect, useMemo, useState } from "react";
import PermissionButton from "../../shared/PermissionButton";
import { useAssessmentScale } from "../../../hooks/useAssessmentScale";
import { Spinner } from "reactstrap";
import Button from "../../shared/Button";
import CustomModal from "../../shared/CustomModal";
import MarksAssessmentForm from "./MarksAssessmentForm";
import LevelsAssessmentScale from "./LevelsAssessmentScale";
import GradeAssessmentScale from "./GradeAssessmentScale";
import { checkRolePermissions } from "../../../../../utils/Constants";
import Pagination from "../../../../../components/UI/Pagination";
import { Tooltip } from "@mui/material";

const ListAssessmentScale = ({ name }) => {
  const {
    assessmentScale,
    totalRecordsCount,
    loading,
    refetch,
    deleteAssessmentScale,
    updateAssessmentScale,
    updateAssessmentScaleWithImage,
  } = useAssessmentScale(null, 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedAssessmentScale, setSelectedAssessmentScale] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [permissions, setPermissions] = useState({
    permissionDeleteAssessmentScale: false,
    permissionEditAssessmentScale: false,
  });

  //generate grades A, A-, A+
  const gradeSelectOptions = useMemo(() => {
    const grades = [];

    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i);
      grades.push(letter, `${letter}+`, `${letter}-`);
    }

    return grades.map((g) => ({
      label: g,
      value: g,
    }));
  }, []);

  const checkPermissions = () => {
    setPermissions({
      permissionDeleteAssessmentScale: checkRolePermissions(
        "delete-assessment-scale",
        "activity",
        name,
      ),
      permissionEditAssessmentScale: checkRolePermissions(
        "edit-assessment-scale",
        "activity",
        name,
      ),
    });
  };

  const handleUpdateAssessment = async (data, updateImage = false) => {
    if (updateImage) {
      try {
        await updateAssessmentScaleWithImage(data);
        refetch(currentPage);
        setUpdateModal(false);
      } catch (error) {
        console.error("Update Failed", error);
      }
    } else {
      try {
        await updateAssessmentScale(data);
        refetch(currentPage);
        setUpdateModal(false);
      } catch (error) {
        console.error("Update Failed", error);
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    refetch(page);
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalRecordsCount / pageSize);

    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      refetch(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      refetch(prevPage);
    }
  };

  const handleDeleteAssessmentScale = async () => {
    try {
      await deleteAssessmentScale(selectedAssessmentScale?.id);
      const updatedTotalRecordsCount = totalRecordsCount - 1;
      const totalPages = Math.ceil(updatedTotalRecordsCount / pageSize);
      let page = currentPage;
      if (page > 1 && page > totalPages) {
        page = page - 1;
        setCurrentPage(page);
      }
      refetch(page);
    } catch (error) {
      console.error("Delete Failed: ", error);
    }
    setDeleteModal(false);
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  return (
    <>
      <div className="arr-content">
        <div className="arr-heading">
          <h1>List Assessment Scale</h1>
          <div className="arr-btns-group">
            <PermissionButton
              moduleName={"Recording Setup"}
              activityName={"select-assessment-scale"}
              widgetName={name}
              header={"List Assessment Scale"}
            />
          </div>
        </div>

        <div className="arr-table-container">
          <table aria-label="arr-table" className="arr-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Assessment Scale Type</th>
                <th>Scale Name</th>
                <th>Score Name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading.read ? (
                <tr>
                  <td colSpan={5}>
                    <div className="arr-spinner">
                      <div className="loader">
                        <Spinner size={"md"} color="primary"></Spinner>
                      </div>
                      <div style={{ fontSize: "13px", marginTop: "15px" }}>
                        Please wait !! Processing.....
                      </div>
                    </div>
                  </td>
                </tr>
              ) : assessmentScale?.length > 0 ? (
                assessmentScale?.map((item, index) => (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>
                      {item.assessmentScaleTypeCode === "sct-mrk"
                        ? "Mark"
                        : item.assessmentScaleTypeCode === "sct-lvl"
                          ? "Level"
                          : item.assessmentScaleTypeCode === "sct-grd"
                            ? "Grade"
                            : "N/A"}
                    </td>
                    <td>{item.scaleName}</td>
                    <td>{item.scoreName}</td>
                    <td>
                      <div className="action-btns">
                        <Button
                          onClick={() => {
                            setSelectedAssessmentScale(item);
                            setViewModal((prev) => !prev);
                          }}
                          size="sm"
                          variant="outline"
                        >
                          View
                        </Button>
                        {permissions.permissionEditAssessmentScale && (
                          <Button
                            onClick={() => {
                              setSelectedAssessmentScale(item);
                              setUpdateModal((prev) => !prev);
                            }}
                            size="sm"
                            variant="outline"
                          >
                            Update
                          </Button>
                        )}
                        {permissions.permissionDeleteAssessmentScale && (
                          <Tooltip
                            title={
                              item?.isConfigured
                                ? "You cannot delete it because it is currently being used in the Subject/General Configuration."
                                : ""
                            }
                          >
                            <div>
                              <Button
                                onClick={() => {
                                  setSelectedAssessmentScale(item);
                                  setDeleteModal((prev) => !prev);
                                }}
                                disabled={item?.isConfigured}
                                size="sm"
                                variant="danger"
                              >
                                Delete
                              </Button>
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          className="modern-pagination"
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "48px",
          }}
        >
          <Pagination
            type="modern"
            itemsCount={totalRecordsCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            nextPage={handleNextPage}
            previousPage={handlePreviousPage}
          />
        </div>
      </div>

      {/* delete Modal */}
      <CustomModal
        isOpen={deleteModal}
        toggle={() => setDeleteModal((prev) => !prev)}
        modalHeader={"Are you sure ?"}
        children={
          <div>
            <p>
              Once deleted, you won't be able to recover this assessment scale.
            </p>

            <div className="action-btns end">
              <Button
                variant="outline"
                onClick={() => setDeleteModal((prev) => !prev)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={loading.delete}
                onClick={handleDeleteAssessmentScale}
              >
                {loading.delete ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Delete"
                )}
              </Button>
            </div>
          </div>
        }
      />

      {/* Edit Modal */}
      <CustomModal
        size="md"
        isOpen={updateModal}
        toggle={() => setUpdateModal((prev) => !prev)}
        modalHeader={"Update Assessment Scale"}
      >
        {selectedAssessmentScale?.assessmentScaleTypeCode === "sct-mrk" ? (
          <MarksAssessmentForm
            type="edit"
            scaleType={selectedAssessmentScale?.assessmentScaleTypeCode}
            updating={loading.update}
            handleUpdateAssessment={handleUpdateAssessment}
            data={selectedAssessmentScale}
            closeModal={() => setUpdateModal(false)}
          />
        ) : selectedAssessmentScale?.assessmentScaleTypeCode === "sct-lvl" ? (
          <LevelsAssessmentScale
            type="edit"
            data={selectedAssessmentScale}
            scaleType={selectedAssessmentScale?.assessmentScaleTypeCode}
            updating={loading.update}
            handleUpdateAssessment={handleUpdateAssessment}
            gradeSelectOptions={gradeSelectOptions}
            closeModal={() => setUpdateModal(false)}
          />
        ) : selectedAssessmentScale?.assessmentScaleTypeCode === "sct-grd" ? (
          <GradeAssessmentScale
            type="edit"
            data={selectedAssessmentScale}
            scaleType={selectedAssessmentScale?.assessmentScaleTypeCode}
            updating={loading.update}
            handleUpdateAssessment={handleUpdateAssessment}
            gradeSelectOptions={gradeSelectOptions}
            closeModal={() => setUpdateModal(false)}
          />
        ) : null}
      </CustomModal>

      {/* View Modal */}
      <CustomModal
        size="md"
        isOpen={viewModal}
        toggle={() => setViewModal((prev) => !prev)}
        modalHeader={"View Assessment Scale"}
      >
        {selectedAssessmentScale?.assessmentScaleTypeCode === "sct-mrk" ? (
          <MarksAssessmentForm type="view" data={selectedAssessmentScale} />
        ) : selectedAssessmentScale?.assessmentScaleTypeCode === "sct-lvl" ? (
          <LevelsAssessmentScale
            type="view"
            data={selectedAssessmentScale}
            gradeSelectOptions={gradeSelectOptions}
          />
        ) : selectedAssessmentScale?.assessmentScaleTypeCode === "sct-grd" ? (
          <GradeAssessmentScale
            type="view"
            data={selectedAssessmentScale}
            gradeSelectOptions={gradeSelectOptions}
          />
        ) : null}
      </CustomModal>
    </>
  );
};

export default ListAssessmentScale;
